import { useEffect, useState } from 'react'
import { useWebPrices, useWebContent, czk, type WebPrices } from '../lib/webPrices'
import { assetUrl } from '../lib/api'
import { penzionUrlFrom } from '../lib/booking'

type Unit = {
  id: string
  name: string
  image: string
  persons: string
  size?: string
  bedrooms?: string
  priceFrom: string
  features: string[]
  resType: string
  /** Časy příjezdu/odjezdu (pravidla areálu) */
  times: string
  gallery?: string[]
}

const UNITS: Unit[] = [
  {
    id: 'penzion',
    name: 'Pokoj penzionu',
    image: '/img/penzion_01_zakouti_kreslo.jpg',
    persons: 'až 4 osoby',
    size: '20–28 m²',
    bedrooms: '1 ložnice + obývací',
    priceFrom: 'od 1 200 Kč / noc',
    features: ['Vlastní koupelna', 'TV · Wi-Fi', 'Klimatizace · minilednice', 'Snídaně možná'],
    resType: 'penzion',
    times: 'Příjezd od 14:00 · odjezd do 10:00',
    gallery: [
      '/img/penzion_01_zakouti_kreslo.jpg',
      '/img/penzion_02_pokoj_postele.jpg',
      '/img/penzion_04_zakouti_okno.jpg',
      '/img/penzion_03_koupelna.jpg',
    ],
  },
  {
    id: 'chatka',
    name: 'Rybářská chatka',
    image: '/img/chatka_01_chatka_sunset.jpg',
    persons: '2–4 osoby',
    size: '15 m²',
    bedrooms: '2 patrové postele',
    priceFrom: 'od 450 Kč / noc',
    features: ['Přímo u rybníka · vlastní břeh', 'Krytá veranda s posezením', 'Lednice na chatce', 'Wi-Fi · parkování v ceně', 'Společné WC a sprcha'],
    resType: 'chatka',
    times: 'Příjezd od 16:00 · odjezd do 14:00 (rybolov až do 16:00)',
    gallery: [
      '/img/chatka_01_chatka_sunset.jpg',
      '/img/chatka_02_rybnik_sunset.jpg',
    ],
  },
  {
    id: 'tiny-house',
    name: 'Tiny House',
    image: '/img/tinyhouse_01_sauna_hottub.jpg',
    persons: '2–4 osoby',
    size: '12–20 m²',
    bedrooms: 'Dvojlůžko + posezení',
    priceFrom: 'od 1 800 Kč / noc',
    features: ['Designový domek pod hvězdami', 'Plovoucí sauna + hot tub', 'Láhev prosecca 0,7 l na pokoji', 'Vlastní koupelna · kuchyňka', 'Wi-Fi · klimatizace · topení', 'Terasa u rybníka · parkování zdarma', 'Minimálně 2 noci'],
    resType: 'glamping_tiny',
    times: 'Příjezd od 15:00 · odjezd do 12:00',
    gallery: [
      '/img/tinyhouse_01_sauna_hottub.jpg',
      '/img/tinyhouse_02_sauna_interier.jpg',
      '/img/tinyhouse_03_sauna_dalka.jpg',
      '/img/tinyhouse_04_sauna_layout.jpg',
      '/img/tinyhouse_05_sauna_kamna.jpg',
      '/img/tinyhouse_06_sauna_okno.jpg',
      '/img/tinyhouse_07_glamping_postel.jpg',
      '/img/tinyhouse_08_exterier_predni.jpg',
      '/img/tinyhouse_09_exterier_bok.jpg',
      '/img/tinyhouse_10_exterier_zezadu.jpg',
      '/img/tinyhouse_11_obyvak.jpg',
      '/img/tinyhouse_12_interier_terasa.jpg',
      '/img/tinyhouse_13_kuchyne.jpg',
      '/img/tinyhouse_14_okno_posezeni.jpg',
      '/img/tinyhouse_15_loznice_police.jpg',
      '/img/tinyhouse_16_loznice_postel.jpg',
      '/img/tinyhouse_17_loznice_vyhled.jpg',
      '/img/tinyhouse_18_koupelna.jpg',
      '/img/tinyhouse_19_terasa_vino.jpg',
    ],
  },
  {
    id: 'bivak',
    name: 'Bivak',
    image: '/img/bivak_01_zapad.webp',
    persons: 'vlastní vybavení',
    bedrooms: 'Vlastní stan / bivak',
    priceFrom: '100 Kč / 12 h slot',
    features: ['7 míst · celoročně', 'Sprcha · WC', 'Wi-Fi · parkování zdarma', 'El. přípojka +200 Kč / 24 h'],
    resType: 'kemp',
    times: 'Sloty 12 h: 7:00–19:00 nebo 19:00–7:00',
  },
  {
    id: 'karavan',
    name: 'Karavan',
    image: '/img/karavan_01_zapad.webp',
    persons: 'vlastní karavan',
    bedrooms: '—',
    priceFrom: 'od 290 Kč / 24 h',
    features: ['4 stání', 'El. přípojka +200 Kč / 24 h', 'Voda · WC · Wi-Fi', 'Vyrovnaný terén'],
    resType: 'karavan',
    times: 'Příjezd od 12:00 · odjezd do 12:00',
  },
]

function unitPrice(id: string, p: WebPrices, fallback: string): string {
  switch (id) {
    case 'penzion': return p.penzion_od ? `od ${czk(p.penzion_od)} Kč / noc` : fallback
    case 'chatka': return p.chatka_od ? `od ${czk(p.chatka_od)} Kč / noc` : fallback
    case 'tiny-house': return p.tiny_od ? `od ${czk(p.tiny_od)} Kč / noc` : fallback
    case 'bivak': return p.bivak_od ? `${czk(p.bivak_od)} Kč / 12 h slot` : fallback
    case 'karavan': return p.karavan_od ? `od ${czk(p.karavan_od)} Kč / 24 h` : fallback
    default: return fallback
  }
}

function AccommodationIntro() {
  const content = useWebContent()
  return (
    <p className="mt-5 text-ink-700 text-base md:text-lg">
      {(content.accommodation_intro as string) ||
        'Komfortní pokoje penzionu přímo u 3 ha rybníka. Rybářské chatky, Tiny House, bivak a karavan najdete v sekci Rybolov.'}
    </p>
  )
}

// Robin 5. 11. 2026: Ubytování = penzion; Rybolov = chatky, Tiny House, bivak, karavan (vlastní sekce #rybolov)
const STAY_IDS = ['penzion']
const FISH_IDS = ['chatka', 'tiny-house', 'bivak', 'karavan']

export function Accommodation() {
  const content = useWebContent()
  const stay = UNITS.filter((u) => STAY_IDS.includes(u.id))
  const fish = UNITS.filter((u) => FISH_IDS.includes(u.id))
  return (
    <>
      <section id="ubytovani" className="py-20 md:py-28 bg-white scroll-mt-24">
        <div className="container-x">
          <div className="max-w-3xl">
            <p className="h-eyebrow">Ubytování</p>
            <h2 className="h-display text-3xl md:text-[44px] mt-4">
              Ubytování v&nbsp;areálu <span className="h-accent">Sobáčov</span>
            </h2>
            <AccommodationIntro />
          </div>

          <div className={`mt-14 grid gap-6 lg:gap-8 ${stay.length > 1 ? 'md:grid-cols-2' : 'max-w-2xl'}`}>
            {stay.map((u) => (
              <UnitCard key={u.id} u={u} />
            ))}
          </div>
        </div>
      </section>

      <section id="rybolov" className="py-20 md:py-28 bg-paper border-y border-ink-900/[0.06] scroll-mt-24">
        <div className="container-x">
          <div className="max-w-3xl">
            <p className="h-eyebrow">Rybolov</p>
            <h2 className="h-display text-3xl md:text-[44px] mt-4">
              Rybolov v&nbsp;<span className="h-accent">Sobáčově</span>
            </h2>
            <p className="mt-5 text-ink-700 text-base md:text-lg">
              {(content.fishing_intro as string) ||
                'Rybářské chatky s vlastním břehem, Tiny House u vody, bivak na 12hodinové sloty a stání pro karavan — vše přímo u 3 ha rybníka. Povolenku vystavíme na místě.'}
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[13px] font-semibold">
              <a href="#volny-cas-rybolov" className="text-brand-600 hover:text-brand-700">O rybolovu u nás →</a>
              <a href="#pravidla-rybolov" className="text-brand-600 hover:text-brand-700">Pravidla rybolovu →</a>
            </div>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
            {fish.map((u) => (
              <UnitCard key={u.id} u={u} />
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

function UnitCard({ u }: { u: Unit }) {
  const [galleryOpen, setGalleryOpen] = useState(false)
  const prices = useWebPrices()
  const content = useWebContent()
  const penzionUrl = u.id === 'penzion' ? penzionUrlFrom(content as Record<string, unknown>) : null   // penzion dočasně přes Booking.com
  const cardImage = assetUrl(content[`unit_image_${u.id}`] as string) || u.image
  const featuresOverride = content[`unit_features_${u.id}`] as string | undefined
  const features = featuresOverride
    ? featuresOverride.split('\n').map((x) => x.trim()).filter(Boolean)
    : u.features
  function openReservation() {
    document.dispatchEvent(
      new CustomEvent('sobacov:open-reservation', {
        detail: { type: u.resType, kind: 'stay', label: u.name },
      }),
    )
  }
  return (
    <article className="group card transition-shadow flex flex-col" id={`ubytovani-${u.id}`}>
      <button
        onClick={() => u.gallery && u.gallery.length > 0 && setGalleryOpen(true)}
        className={`relative overflow-hidden block text-left group/img ${u.gallery ? 'cursor-zoom-in' : 'cursor-default'}`}
        disabled={!u.gallery}
      >
        <img
          src={cardImage}
          alt={u.name}
          className="w-full aspect-[4/3] object-cover group-hover/img:scale-[1.04] transition-transform duration-[700ms] ease-out"
          loading="lazy"
        />
        {u.gallery && u.gallery.length > 0 && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm text-ink-900 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg shadow-md">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            {u.gallery.length} {u.gallery.length === 1 ? 'fotka' : u.gallery.length < 5 ? 'fotky' : 'fotek'}
          </span>
        )}
      </button>
      {galleryOpen && u.gallery && (
        <GalleryLightbox photos={u.gallery} title={u.name} onClose={() => setGalleryOpen(false)} />
      )}
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-bold text-xl text-ink-900 tracking-tight">{u.name}</h3>

        <dl className="mt-4 grid grid-cols-3 gap-2 text-center text-xs text-ink-700 border-y border-ink-900/[0.08] py-3">
          <div>
            <dt className="text-[10px] uppercase tracking-[0.12em] text-ink-700/60 font-semibold">Kapacita</dt>
            <dd className="mt-1 font-semibold text-ink-900">{u.persons}</dd>
          </div>
          {u.size && (
            <div>
              <dt className="text-[10px] uppercase tracking-[0.12em] text-ink-700/60 font-semibold">Velikost</dt>
              <dd className="mt-1 font-semibold text-ink-900">{u.size}</dd>
            </div>
          )}
          <div>
            <dt className="text-[10px] uppercase tracking-[0.12em] text-ink-700/60 font-semibold">Pokoje</dt>
            <dd className="mt-1 font-semibold text-ink-900">{u.bedrooms || '—'}</dd>
          </div>
        </dl>

        <p className="mt-3 flex items-start gap-2 text-[12.5px] text-ink-700">
          <svg viewBox="0 0 24 24" className="w-4 h-4 mt-[1px] shrink-0 text-brand-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          <span>{(content[`unit_times_${u.id}`] as string) || u.times}</span>
        </p>

        <ul className="mt-4 space-y-1.5 text-sm text-ink-800 flex-1">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <svg viewBox="0 0 24 24" className="w-4 h-4 mt-0.5 shrink-0 text-brand-500" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12.5l5 5L20 6.5" />
              </svg>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 pt-4 border-t border-ink-900/[0.08] flex items-center justify-between gap-3">
          <div className="text-ink-900 font-bold text-[17px]">
            {penzionUrl ? <span className="text-[13px] text-ink-700 font-semibold">ceny a dostupnost na Booking.com</span> : unitPrice(u.id, prices, u.priceFrom)}
          </div>
          {penzionUrl ? (
            <a href={penzionUrl} target="_blank" rel="noopener" className="btn-book text-[14px] py-2 px-4 whitespace-nowrap">
              Rezervovat na Booking.com ↗
            </a>
          ) : (
            <button
              onClick={openReservation}
              className="btn-book text-[14px] py-2 px-4"
            >
              Rezervovat
            </button>
          )}
        </div>
      </div>
    </article>
  )
}

/* ─────────────────────── Gallery lightbox (carousel) ─────────────────────── */

function GalleryLightbox({ photos, title, onClose }: { photos: string[]; title: string; onClose: () => void }) {
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % photos.length)
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + photos.length) % photos.length)
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [photos.length, onClose])

  function next() { setIdx((i) => (i + 1) % photos.length) }
  function prev() { setIdx((i) => (i - 1 + photos.length) % photos.length) }

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="fixed inset-0 z-[110] bg-ink-900/95 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 text-white">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/55">Galerie</div>
          <div className="text-lg font-bold tracking-tight mt-0.5">{title}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm tabular-nums opacity-80">
            {idx + 1} / {photos.length}
          </div>
          <button onClick={onClose} className="w-10 h-10 grid place-items-center bg-white/10 hover:bg-white/20 text-2xl">
            ✕
          </button>
        </div>
      </div>

      {/* Hlavní fotka */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center px-2 sm:px-12">
        <button
          onClick={prev}
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 grid place-items-center bg-white/10 hover:bg-white/20 text-white text-2xl"
          aria-label="Předchozí"
        >
          ‹
        </button>
        <img
          src={photos[idx]}
          alt=""
          className="max-w-full max-h-full object-contain"
        />
        <button
          onClick={next}
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 grid place-items-center bg-white/10 hover:bg-white/20 text-white text-2xl"
          aria-label="Další"
        >
          ›
        </button>
      </div>

      {/* Thumbnails */}
      <div className="bg-ink-900 border-t border-white/10 p-2 overflow-x-auto">
        <div className="flex gap-1.5">
          {photos.map((p, i) => (
            <button
              key={p}
              onClick={() => setIdx(i)}
              className={`flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 overflow-hidden transition ${
                idx === i ? 'ring-2 ring-brand-500' : 'opacity-60 hover:opacity-100'
              }`}
            >
              <img src={p} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
